import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { PublishRounded, Warning } from "@mui/icons-material";

const PublishAsTemplateConfirmDialog = ({
  open,
  isDarkMode,
  isPublishing,
  agentName,
  chainedAgentCount,
  publishedChainedCount,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 600,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: isDarkMode
            ? "rgba(255,255,255,0.02)"
            : "rgba(0,0,0,0.02)",
        }}
      >
        Publish Agent as Template
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 600,
              mb: 1.5,
              color: isDarkMode ? "#ffffff" : "#111827",
            }}
          >
            Ready to publish this agent as a template?
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontFamily: '"Poppins", sans-serif',
              color: isDarkMode ? "#9ca3af" : "#4b5563",
              maxWidth: 520,
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Publishing as a template will make your agent configuration available
            for reuse by other users or in other projects. This helps standardize
            agent setups and share proven configurations.
            {agentName ? `\n\nAgent: ${agentName}` : ""}
          </Typography>

          {/* Warning message for chained agents */}
          {chainedAgentCount > 0 && (
            <Alert
              severity="warning"
              icon={<Warning />}
              sx={{
                mt: 3,
                maxWidth: 520,
                mx: "auto",
                backgroundColor: isDarkMode
                  ? "rgba(255, 152, 0, 0.1)"
                  : "rgba(255, 152, 0, 0.05)",
                border: `1px solid ${isDarkMode ? "rgba(255, 152, 0, 0.3)" : "rgba(255, 152, 0, 0.2)"}`,
                "& .MuiAlert-icon": {
                  color: "#ff9800",
                },
                "& .MuiAlert-message": {
                  color: isDarkMode ? "#ffb74d" : "#e65100",
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 500,
                  color: "inherit",
                }}
              >
                Warning: This agent has {chainedAgentCount} chained agent{chainedAgentCount !== 1 ? "s" : ""}. 
                {publishedChainedCount > 0 && publishedChainedCount === chainedAgentCount ? (
                  <> All {publishedChainedCount} chained agent{publishedChainedCount !== 1 ? "s have" : " has"} also been published as template{publishedChainedCount !== 1 ? "s" : ""} to ensure the workflow functions correctly.</>
                ) : publishedChainedCount > 0 ? (
                  <> All {publishedChainedCount} chained agent{publishedChainedCount !== 1 ? "s have" : " has"} also been published as template{publishedChainedCount !== 1 ? "s" : ""} to ensure the workflow functions correctly.</>
                ) : (
                  <> All {chainedAgentCount} chained agent{chainedAgentCount !== 1 ? "s will" : " will"} also be published as template{chainedAgentCount !== 1 ? "s" : ""} to ensure the workflow functions correctly.</>
                )}
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isPublishing}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isPublishing}
          startIcon={
            isPublishing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PublishRounded />
            )
          }
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 700,
            minWidth: 180,
            backgroundColor: "#e36250",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#d14e3c",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(227, 98, 80, 0.5)",
              color: "rgba(255, 255, 255, 0.7)",
            },
          }}
        >
          {isPublishing ? "Publishing..." : "Yes, publish as template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishAsTemplateConfirmDialog;

